import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import clsx from 'clsx'
import {
  cloneElement,
  forwardRef,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from 'react'
import { SolidButtonSpinner } from './SolidButtonSpinner'

const buttonVariants = cva(
  [
    'font-manrope',
    'focus-visible:outline-none focus-visible:ring-1',
    'group transition-all duration-300',
    'min-w-content inline-flex items-center justify-center rounded-lg',
    'disabled:bg-surface-disabled disabled:text-text-400 disabled:pointer-events-none disabled:cursor-not-allowed',
  ],
  {
    variants: {
      variant: {
        primary: 'text-text-800 font-bold',
        secondary:
          'hover:text-primary-600 text-text-800 active:bg-primary-600 active:text-text-50 bg-white font-bold',
        tertiary:
          'text-text-800 active:text-text-50 bg-[#FFD900] font-bold hover:bg-[#FFBF00] active:bg-[#D2850B]',
        textlink:
          'text-primary-700 active:text-primary-800 font-normal shadow-none hover:underline active:underline',
        destructive:
          'bg-error-600 text-text-50 hover:bg-error-700 hover:text-text-50 active:bg-error-800 font-bold',
      },
      outlined: {
        true: 'disabled:border-surface-disabled border disabled:bg-transparent',
        false: '',
      },
      elevated: {
        true: 'shadow-button disabled:shadow-none',
      },
      size: {
        nosize: 'gap-1 px-2',
        sm: 'text-body-xs gap-1 py-2',
        md: 'text-body-sm gap-1.5 py-2.5',
        lg: 'text-body-md gap-2 py-3',
      },
      position: {
        undefined: '',
        left: '',
        right: '',
      },
      loading: {
        true: 'pointer-events-none cursor-not-allowed',
        false: '',
      },
    },
    compoundVariants: [
      { position: undefined, size: 'sm', class: 'px-4' },
      { position: undefined, size: 'md', class: 'px-5' },
      { position: undefined, size: 'lg', class: 'px-6' },
      { position: 'left', size: 'sm', class: 'pl-[14px] pr-4' },
      { position: 'left', size: 'md', class: 'pl-4 pr-5' },
      { position: 'left', size: 'lg', class: 'pl-5 pr-6' },
      { position: 'right', size: 'sm', class: 'pl-4 pr-[14px]' },
      { position: 'right', size: 'md', class: 'pl-5 pr-4' },
      { position: 'right', size: 'lg', class: 'pl-6 pr-5' },
      {
        outlined: true,
        variant: 'primary',
        class: [
          'border-primary-500 hover:bg-surface-brand active:border-primary-500 active:bg-primary-400',
          'disabled:bg-white',
        ],
      },
      {
        outlined: false,
        variant: 'primary',
        class:
          'bg-primary-500 hover:bg-primary-600 active:bg-primary-800 hover:text-white',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      elevated: true,
      outlined: false,
    },
  },
)

export type ButtonVariants = VariantProps<typeof buttonVariants>
export type ButtonVariant = NonNullable<ButtonVariants['variant']>

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  ButtonVariants & {
    asChild?: true
    'data-testid'?: string
    disabled?: boolean
    icon?: IconDefinition
    iconStyle?: string
    label?: string | null
  }

const SolidButton = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      asChild,
      children,
      className,
      'data-testid': dataTestId = 'solidButton',
      disabled,
      loading,
      elevated,
      icon,
      iconStyle,
      label,
      outlined,
      position,
      size,
      variant = 'primary',
      ...props
    },
    ref,
  ) => {
    const Comp = asChild && isReactElement(children) ? Slot : 'button'
    const isEffectivelyDisabled = loading || disabled

    const iconVariant = (iconPosition: string) =>
      iconPosition === position && icon ? (
        <FontAwesomeIcon className={iconStyle} data-testid='icon' icon={icon} />
      ) : null

    return (
      <Comp
        aria-disabled={isEffectivelyDisabled}
        className={clsx(
          buttonVariants({
            elevated,
            outlined,
            position,
            size,
            variant,
            loading,
          }),
          className,
        )}
        data-loading={loading}
        data-testid={dataTestId}
        disabled={disabled}
        ref={ref}
        {...props}
      >
        {asChild && isReactElement<PropsWithChildren>(children) ? (
          cloneElement(
            children,
            {},
            iconVariant('left'),
            children.props.children,
            iconVariant('right'),
            loading ? SolidButtonSpinner({ variant }) : null,
          )
        ) : (
          <>
            {iconVariant('left')}
            {label || children}
            {iconVariant('right')}
            {loading ? <SolidButtonSpinner variant={variant} /> : null}
          </>
        )}
      </Comp>
    )
  },
)

SolidButton.displayName = 'SolidButton'

function isReactElement<P = unknown>(
  element: ReactNode,
): element is ReactElement<P> {
  return element !== null && typeof element === 'object' && 'props' in element
}

export { buttonVariants, SolidButton }
